import { useState, useEffect, useCallback } from "react";
import { RefreshCw, Pause, Play, CheckCircle2, AlertTriangle, Loader2, Cloud } from "lucide-react";
import { toast } from "sonner";
import apiClient from "../../services/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { formatDateTime } from "../../lib/formatters";

const SETUP_STEPS = [
  "Azure portal → App registrations → “V2R Masonry Notifications” → API permissions → Add → APIs my organization uses → “Office 365 Exchange Online” → Application permissions → Exchange.ManageAsApp → Grant admin consent.",
  "Entra admin center → Roles & administrators → “Exchange Recipient Administrator” → Add assignment → pick the app “V2R Masonry Notifications”.",
  "Click “Check connection” below. New contacts then sync right after save; a full reconcile runs daily at 5:00 PM Central.",
];

const ContactsSyncCard = ({ isAdmin, onSynced }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState("");
  const [showSetup, setShowSetup] = useState(false);

  const load = useCallback(async () => {
    try { setStatus((await apiClient.get("/shared-contacts/sync/status")).data); } catch { /* card stays quiet */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "verify") {
        const r = (await apiClient.post("/shared-contacts/sync/verify")).data;
        r.ok ? toast.success(r.dry_run ? "Exchange connection OK (preview dry-run — nothing is written)" : "Exchange connection OK") : toast.error(r.error, { duration: 12000 });
      } else if (kind === "run") {
        const r = (await apiClient.post("/shared-contacts/sync/run")).data;
        r.ok ? toast.success(`Synced: ${r.pushed} pushed, ${r.imported} imported, ${r.deleted} removed`) : toast.error(r.error || `Sync finished with ${r.errors} error(s)`, { duration: 10000 });
        onSynced?.();
      } else if (kind === "pause") {
        const r = (await apiClient.put("/shared-contacts/sync/pause", { paused: !status?.paused })).data;
        toast.success(r.paused ? "Sync paused — add contacts freely, they'll go out when you resume" : "Sync resumed — pending contacts are syncing now");
        if (!r.paused) setTimeout(() => onSynced?.(), 3000);
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Request failed");
    } finally { setBusy(""); }
  };

  if (!status) return null;
  const c = status.counts || {};
  const lastOk = status.last_run?.ok;

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3" data-testid="contacts-sync-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Cloud className="w-4 h-4 text-sky-600" /> Outlook address list (Exchange) sync
          {status.paused ? <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200" data-testid="sync-paused-badge">Paused</Badge>
            : !status.configured ? <Badge variant="outline" className="bg-gray-100 text-gray-700" data-testid="sync-unconfigured-badge">Not configured</Badge>
            : status.dry_run ? <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200" data-testid="sync-dryrun-badge">Preview dry-run</Badge>
            : <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="sync-active-badge">Active</Badge>}
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => run("verify")} disabled={!!busy} data-testid="sync-verify-btn">
              {busy === "verify" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />} Check connection
            </Button>
            <Button size="sm" variant="outline" onClick={() => run("run")} disabled={!!busy || !status.configured} data-testid="sync-run-btn">
              {busy === "run" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />} Sync now
            </Button>
            <Button size="sm" variant={status.paused ? "default" : "outline"} onClick={() => run("pause")} disabled={!!busy} data-testid="sync-pause-btn">
              {status.paused ? <Play className="w-4 h-4 mr-1" /> : <Pause className="w-4 h-4 mr-1" />} {status.paused ? "Resume sync" : "Pause sync"}
            </Button>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground" data-testid="sync-counts">
        <span>{c.synced || 0} in Outlook</span>
        <span>{c.pending || 0} pending</span>
        {c.error > 0 && <span className="text-red-600 font-medium">{c.error} failed</span>}
        {c.local > 0 && <span>{c.local} app-only (no email)</span>}
        {status.pending_deletes > 0 && <span>{status.pending_deletes} removal(s) pending</span>}
        <span>{status.schedule}</span>
        {status.last_run_at && (
          <span className="flex items-center gap-1">
            {lastOk ? <CheckCircle2 className="w-3 h-3 text-emerald-600" /> : <AlertTriangle className="w-3 h-3 text-amber-600" />}
            Last run {formatDateTime(status.last_run_at)}{status.last_run?.error ? ` — ${status.last_run.error}` : ""}
          </span>
        )}
      </div>
      {status.paused && <p className="text-xs text-amber-700">Paused: contacts you add are saved in the app and pushed to Outlook in one batch when you resume.</p>}
      {isAdmin && (
        <button type="button" className="text-xs text-sky-700 hover:underline" onClick={() => setShowSetup((s) => !s)} data-testid="sync-setup-toggle">
          {showSetup ? "Hide" : "Show"} one-time Microsoft 365 setup
        </button>
      )}
      {showSetup && (
        <ol className="list-decimal pl-5 space-y-1 text-xs text-muted-foreground" data-testid="sync-setup-steps">
          {SETUP_STEPS.map((s) => <li key={s.slice(0, 24)}>{s}</li>)}
          <li>Production settings must include <code>M365_MAIL_DRY_RUN=false</code>; requests route through <code>{status.anchor_mailbox}</code>.</li>
        </ol>
      )}
    </div>
  );
};

export default ContactsSyncCard;
